import { Meeting } from '../stores/reducers/session';
import { getAuthenticatedRequestInit, getSyncHeaders } from './utils';
import { GraphicInterface } from '../stores/reducers/graphics';
import { MediaItem } from '../stores/reducers/media';
import { GeneratedContent } from '../stores/types/content';
import { EntityType } from '../types/enums';
import { appLogger } from '../utils/datadog';
import { getEmbedSafeHeaders } from './auth';
import blpAuth from './blp-auth';
import { RegeneratingContent } from '../stores/types/content';

export const requestGenerateContentTemplates = (
	token: string,
) => fetch(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/generate-content/templates`,
	{
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	},
).then((res) => (res.ok ? res.json() : null)).catch((e) => {
	appLogger.error('error getting content templates, message: ', e?.response?.data?.message, e);
	return null;
});

export const requestGeneratedContent = (
	token: string,
	meeting: Meeting,
) => fetch(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/generate-content/${meeting.meeting}`,
	{
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	},
).then((res) => (res.ok ? res.json() : null)).catch((e) => {
	appLogger.error('error loading generated content for meeting', {}, e);
	return null;
});

export const requestGeneratedContentById = (
	token: string,
	meeting: Meeting,
	id: number,
) => fetch(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/generate-content/${meeting.meeting}/${id}`,
	{
		method: 'GET',
		...getAuthenticatedRequestInit(token),
	},
).then((res) => (res.ok ? res.json() : {})).catch((e) => {
	appLogger.error('error loading generated content for meeting', {}, e);
	return {};
});

export const requestDeleteGeneratedContentById = (
	token: string,
	meeting: Meeting,
	id: number,
) => fetch(
	`${process.env.REACT_APP_API_HOST}/v2/greenroom/generate-content/${meeting.meeting}/${id}`,
	{
		method: 'DELETE',
		...getAuthenticatedRequestInit(token),
	},
).then((res) => (res.ok ? id : 0)).catch((e) => {
	appLogger.error('error deleting generated content: ', {}, e);
	return 0;
});

export const upsertContentRequest = (
	token: string,
	roomId: number,
	userId: number,
	associatedContent: GraphicInterface | MediaItem,
	generatedContent: GeneratedContent,
	initialChipUser?: number
): Promise<boolean> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${roomId}/content/upsert`,
	{
		method: 'POST',
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			userId,
			generatedContent,
			associatedContent,
			initialChipUser
		}),
	},
).then((res) => res.ok).catch((e) => {
	appLogger.error('error upserting content: ', {}, e);
	return false;
});

export const upsertAssociatedContentRequest = (
	token: string,
	roomId: number,
	userId: number,
	associatedContent: GraphicInterface | MediaItem,
): Promise<boolean> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${roomId}/content/upsert-associated`,
	{
		method: 'POST',
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			userId,
			associatedContent,
		}),
	},
).then((res) => res.ok).catch((e) => {
	appLogger.error('error upserting associated content: ', {}, e);
	return false;
});

export const publishDraft = (
	token: string,
	room: number,
	userSession: string,
	overlayIds: number[],
	mediaIds: number[],
): Promise<boolean> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${room}/draft/publish`,
	{
		method: 'PUT',
		headers: getSyncHeaders(token),
		body: JSON.stringify({
			userSession,
			overlayIds,
			mediaIds,
		}),
	},
).then((res) => !!res?.ok).catch((e) => {
	appLogger.error('error publishing draft: ', {}, e);
	return false;
});

export const discardDraft = (
	token: string,
	room: number,
): Promise<boolean> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${room}/draft/discard`,
	{
		method: 'DELETE',
		headers: getSyncHeaders(token),
	},
).then((res) => !!res?.ok).catch((e) => {
	appLogger.error('error clearing draft state: ', {}, e);
	return false;
});

export const getDraftEntities = (
	token: string,
	room: number,
	entityType: EntityType,
): Promise<any[]> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${room}/draft/${entityType}`,
	{
		method: 'GET',
		headers: getSyncHeaders(token),
	},
).then((res) => res.json()).catch((e) => {
	appLogger.error('error getting draft entities: ', {}, e);
	return [];
});

export const regenerateContent = (
	room: number,
	content: number,
): Promise<boolean> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${room}/content/regenerate/${content}`,
	{
		method: 'POST',
		headers: getEmbedSafeHeaders(),
	},
).then((res) => {
	if (!res.ok) {
		appLogger.error('error regenerating content, status: ', res);
		console.log('error regenerating content, status: ', res.status);
		return false;
	}

	return true;
}).catch((e) => {
	appLogger.error('error regenerating content: ', e);
	console.log('error regenerating content: ', e);
	return false;
});

export const regenerateAllContentForUser = (
	room: number,
	userId: number,
): Promise<boolean> => fetch(
	`${process.env.REACT_APP_SYNC_HOST}/v1/room/${room}/content/regenerate/user/${userId}`,
	{
		method: 'POST',
		headers: getEmbedSafeHeaders(),
	},
).then((res) => {
	return true;
}).catch((e) => {
	appLogger.error('error regenerating content: ', e);
	return false;
});

export const RegenerateStaleContentForMeeting = (
	room: number,
	{ themeIds = [], userIds = [] }: { themeIds?: number[], userIds?: number[] } = {},
): Promise<RegeneratingContent[]> => (
	blpAuth.put({
		url: `/v1/room/${room}/content/regenerate-stale`,
		host: process.env.REACT_APP_SYNC_HOST,
		token: blpAuth.accessToken,
		options: { headers: getEmbedSafeHeaders() },
		body: { themeIds, userIds },
	}).then((res) => {
		return res;
	}).catch((err) => {
		console.error('Error', err);
		return false;
	})
);